import { combineLatest, concat, first, fromEvent, interval, mergeMap, shareReplay } from 'rxjs';
import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Auth, authState } from '@angular/fire/auth';

interface BeforeInstallPromptEvent {
  // eslint-able-next-line @typescript-eslint/no-explicit-any
  prompt(): Promise<any>;
}

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  constructor(
    private updates: SwUpdate,
    private appRef: ApplicationRef,
    private auth: Auth,
  ) {
    updates.versionUpdates.subscribe(event => {
      if(event.type === 'VERSION_DETECTED') {
        console.log(`New app version available ${event.version})`)
      } else if(event.type === 'VERSION_READY') {
        console.log(`New app version ready: ${event.latestVersion} (current version ${event.currentVersion})`)
      }
    })

    this.periodicallyCheckForUpdates();

    this.forceUpdateWhenAvailable(); // might delete data halfway through somebody filling out a form

    this.askToInstallOnceLoggedIn()
  }

  private periodicallyCheckForUpdates() {
    // Allow the app to stabilize first, before starting polling for updates with `interval()`.
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true))
    const everyFiveMinutes$ = interval(5 * 60 * 1000)
    const everyFiveMinutesOnceAppIsStable$ = concat(appIsStable$, everyFiveMinutes$)
    everyFiveMinutesOnceAppIsStable$.subscribe(() => {
      console.log("checking for software update...")
      this.updates.checkForUpdate().catch(e => console.warn(e.message))
    })
  }

  private forceUpdateWhenAvailable() {
    this.updates.versionUpdates.subscribe(event => {
      if(event.type === 'VERSION_READY' && confirm('New app version available. Would you like to reload?')) {
        // TODO: would be better to use material modal, so app can keep uploading/working in the background
        this.updates.activateUpdate().then(() => document.location.reload())
      }
    })
  }

  // Booo, this only works in Chrome and is deprecated:
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/onbeforeinstallprompt
  private askToInstallOnceLoggedIn() {
    const installPrompt$ = fromEvent<BeforeInstallPromptEvent>(window, 'beforeinstallprompt').pipe(
      shareReplay(1)
    )
    const iHaveLoggedIn$ =  authState(this.auth).pipe(first(user => !!user))

    const iHaveLoggedInAndAppIsInstallable$ = combineLatest([iHaveLoggedIn$, installPrompt$]).pipe(
      mergeMap(([, event]) => event.prompt())
    )

    iHaveLoggedInAndAppIsInstallable$.subscribe(
      res => console.log('PWA prompt response', res),
      (err: Error) => console.warn('PWA prompt error:', err.message),
      ()  => console.log('PWA prompt request completed OK.')
    )
  }
}
